/**
 * JoinPredicates — small presentational helpers extracted from `JoinLine` to
 * keep the host component within the 200-line budget. Renders the ON pill
 * list (split on top-level AND/OR via `splitTopLevelBoolean`) and the USING
 * column-set pill. Disabled per § 1.4 (display-only in VQE-RO).
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { JoinSpan } from './extractFromJoins';
import { splitTopLevelBoolean } from './splitTopLevel';
import { VqeTooltip } from '../common/VqeTooltip';
import { vqeCopy } from '../../ux/vqeCopy';

const TOOLTIP = vqeCopy.disabled.editingUnavailable;

export interface OnPillsProps {
  readonly bufferText: string;
  readonly on: JoinSpan;
  readonly options: ParseOptions;
  readonly testIdPrefix: string;
}

export function OnPills({ bufferText, on, options, testIdPrefix }: OnPillsProps) {
  const fragments = splitTopLevelBoolean(bufferText, on.start, on.end, options);
  return (
    <>
      <span
        className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
        data-testid={`${testIdPrefix}-on-label`}
      >
        ON
      </span>
      <ul
        className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0"
        data-testid={`${testIdPrefix}-on-pills`}
      >
        {fragments.map((frag, idx) => (
          <li
            key={`${frag.text}-${idx}`}
            className="flex items-center gap-1.5"
            data-testid={`${testIdPrefix}-on-pill-${idx}`}
          >
            {frag.leadingConnector ? (
              <span
                className="rounded bg-muted px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                data-testid={`${testIdPrefix}-on-pill-${idx}-connector`}
              >
                {frag.leadingConnector}
              </span>
            ) : null}
            <VqeTooltip content={TOOLTIP}>
              <span
                className="rounded-full border border-border bg-muted/40 px-2 py-0.5 font-mono text-[11px]"
                data-testid={`${testIdPrefix}-on-pill-${idx}-text`}
              >
                {frag.text}
              </span>
            </VqeTooltip>
          </li>
        ))}
      </ul>
    </>
  );
}

export interface UsingPillProps {
  readonly using: JoinSpan;
  readonly testIdPrefix: string;
}

export function UsingPill({ using, testIdPrefix }: UsingPillProps) {
  return (
    // `shrink` + the pill's own `max-w-full truncate` keep a long column set
    // ellipsized INSIDE the pill's border rather than sliced by an ancestor.
    <VqeTooltip content={`USING ${using.text} · ${TOOLTIP}`} shrink>
      <span
        className="block max-w-full truncate rounded-full border border-blue-500/40 bg-blue-500/15 px-2.5 py-0.5 font-mono text-[11px]"
        data-testid={`${testIdPrefix}-using-pill`}
      >
        USING {using.text}
      </span>
    </VqeTooltip>
  );
}

export interface RhsSpanProps {
  readonly text: string;
  readonly testIdPrefix: string;
}

export function RhsSpan({ text, testIdPrefix }: RhsSpanProps) {
  const isDerived = text.trimStart().startsWith('(');
  const cls = isDerived
    ? 'font-mono text-[11px] text-foreground rounded border border-border bg-muted/40 px-1.5 py-0.5'
    : 'font-mono text-[11px] text-foreground';
  return (
    <span
      className={cls}
      data-testid={`${testIdPrefix}-rhs`}
      data-rhs-kind={isDerived ? 'derived' : 'reference'}
    >
      {text || '?'}
    </span>
  );
}
